import { useModuleData } from "@/hooks/useModuleData";
import { useSite } from "@/context/SiteContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { KpiCard } from "@/components/common/KpiCard";
import { Loader } from "@/components/common/Loader";
import { eur, num } from "@/lib/format";
import {
  BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  TrendingUp, ShoppingCart, Factory, PackageX, Wallet, ShieldCheck, Warehouse, AlertTriangle, Bot,
} from "lucide-react";

const STATUS_COLORS = { RELEASED: "#3B82F6", IN_PROGRESS: "#F59E0B", CONFIRMED: "#10B981", CREATED: "#64748B", TECO: "#06B6D4" };

export default function Dashboard() {
  const { data, loading } = useModuleData("/dashboard");
  const { data: alertData } = useModuleData("/dashboard/alerts");
  const { current } = useSite();
  const { user } = useAuth();
  const navigate = useNavigate();

  if (loading || !data) return <Loader />;
  const k = data.kpis;
  const alerts = alertData?.alerts || [];
  const AS = {
    critical: { bar: "#EF4444", chip: "bg-red-100 text-red-700" },
    warning: { bar: "#F59E0B", chip: "bg-amber-100 text-amber-700" },
    info: { bar: "#0a6ed1", chip: "bg-sky-100 text-sky-700" },
  };

  return (
    <div>
      <div className="mb-6">
        <p className="font-mono text-[11px] tracking-wider text-[#0a6ed1]">
          {current.name} · Live operational scope
        </p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1d2d3e]">
          Command Center
        </h1>
        <p className="mt-1 text-sm text-[#6a6d70]">Welcome back, {user?.name?.split(" ")[0]}. Real-time cross-module overview.</p>
      </div>

      {alerts.length > 0 && (
        <div className="mb-6 rounded-xl border border-[#e5e9f0] bg-white p-4 shadow-sm" data-testid="copilot-alerts">
          <div className="flex items-center gap-2">
            <Bot size={16} className="text-[#0a6ed1]" />
            <h2 className="text-sm font-semibold uppercase tracking-wide text-[#32363a]">Copilot Alerts</h2>
            <span className="rounded-full bg-[#eef1f5] px-2 py-0.5 text-[11px] font-medium text-[#6a6d70]">{alerts.length}</span>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {alerts.map((a) => {
              const s = AS[a.severity] || AS.info;
              return (
                <button key={a.id} data-testid={`alert-${a.id}`} onClick={() => navigate(a.action_path)}
                  className="group flex flex-col rounded-lg border border-[#e5e9f0] bg-[#fbfcfe] p-3 text-left transition-all hover:shadow-md">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-[#32363a]">{a.title}</span>
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${s.chip}`}>{a.module}</span>
                  </div>
                  <span className="mt-1 text-xs text-[#6a6d70]">{a.message}</span>
                  <span className="mt-2 text-[11px] font-medium text-[#0a6ed1] group-hover:underline">{a.action_label} →</span>
                  <span className="mt-2 h-1 rounded-full" style={{ background: s.bar }} />
                </button>);
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="kpi-revenue" label="Sales Value" value={eur(k.revenue)} sub="Across open + booked orders" accent="#8B5CF6" icon={TrendingUp} onClick={() => navigate("/sales")} />
        <KpiCard testId="kpi-open-so" label="Open Sales Orders" value={k.open_sales_orders} sub="Awaiting delivery" accent="#8B5CF6" icon={ShoppingCart} onClick={() => navigate("/sales")} />
        <KpiCard testId="kpi-production" label="Active Production" value={k.active_production} sub="Released / in progress" accent="#3B82F6" icon={Factory} onClick={() => navigate("/production")} />
        <KpiCard testId="kpi-low-stock" label="Low Stock Items" value={k.low_stock_items} sub="Below reorder point" accent="#F97316" icon={PackageX} onClick={() => navigate("/materials")} />
        <KpiCard testId="kpi-spend" label="Procurement Spend" value={eur(k.procurement_spend)} sub="Open purchase orders" accent="#F97316" icon={Wallet} onClick={() => navigate("/purchasing")} />
        <KpiCard testId="kpi-qm" label="QM Pass Rate" value={`${k.qm_pass_rate}%`} sub="Inspection lots" accent="#EAB308" icon={ShieldCheck} onClick={() => navigate("/quality")} />
        <KpiCard testId="kpi-cranes" label="Active ASRS Cranes" value={k.active_cranes} sub="Storing / retrieving" accent="#EC4899" icon={Warehouse} onClick={() => navigate("/warehouse")} />
        <KpiCard testId="kpi-edi" label="EDI Errors" value={k.edi_errors} sub="Require reprocessing" accent="#EF4444" icon={AlertTriangle} onClick={() => navigate("/edi")} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-xl border border-[#e5e9f0] bg-[#ffffff] p-5">
          <h3 className="text-lg font-semibold text-[#32363a]">Sales Value by Site</h3>
          <div className="mt-4 h-72" style={{ minHeight: 288 }}>
            <ResponsiveContainer width="100%" height="100%" minHeight={260}>
              <BarChart data={data.revenue_by_site}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e9f0" />
                <XAxis dataKey="site" stroke="#64748B" fontSize={11} />
                <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => `£${num(v / 1000)}k`} />
                <Tooltip contentStyle={{ background: "#eef1f5", border: "1px solid #e5e9f0", borderRadius: 8, color: "#32363a" }}
                  formatter={(v) => eur(v)} />
                <Bar dataKey="revenue" fill="#0a6ed1" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-[#e5e9f0] bg-[#ffffff] p-5">
          <h3 className="text-lg font-semibold text-[#32363a]">Production Order Status</h3>
          <div className="mt-4 h-72" style={{ minHeight: 288 }}>
            <ResponsiveContainer width="100%" height="100%" minHeight={260}>
              <PieChart>
                <Pie data={data.production_status} dataKey="count" nameKey="status" cx="50%" cy="50%"
                  innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {data.production_status.map((e) => <Cell key={e.status} fill={STATUS_COLORS[e.status] || "#64748B"} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#eef1f5", border: "1px solid #e5e9f0", borderRadius: 8, color: "#32363a" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 flex flex-wrap gap-3">
            {data.production_status.map((e) => (
              <div key={e.status} className="flex items-center gap-1.5 text-xs text-[#6a6d70]">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS_COLORS[e.status] || "#64748B" }} />
                {e.status.replace(/_/g, " ")} ({e.count})
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
